import { authService, AuthError } from "../services/authService";
import { config } from "../config";
import { byId, hide, qsAll, show } from "./dom";

type AuthViewName = "login" | "register" | "forgot" | "reset-sent" | "reset-confirm";

export interface AuthViewController {
  showResetConfirm(): void;
}

export function initAuthView(onAuthenticated: () => void): AuthViewController {
  const shell = byId("auth-shell");
  const views: Record<AuthViewName, HTMLElement> = {
    login: byId("auth-view-login"),
    register: byId("auth-view-register"),
    forgot: byId("auth-view-forgot"),
    "reset-sent": byId("auth-view-reset-sent"),
    "reset-confirm": byId("auth-view-reset-confirm"),
  };

  function switchTo(name: AuthViewName): void {
    (Object.keys(views) as AuthViewName[]).forEach((key) => {
      views[key].hidden = key !== name;
    });
    clearErrors();
  }

  function clearErrors(): void {
    qsAll<HTMLElement>(".auth-error", shell).forEach((el) => {
      el.hidden = true;
      el.textContent = "";
    });
  }

  function showError(viewName: AuthViewName, message: string): void {
    const el = views[viewName].querySelector<HTMLElement>(".auth-error");
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  function setBusy(form: HTMLFormElement, busy: boolean): void {
    const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (!btn) return;
    btn.disabled = busy;
    btn.dataset.label ??= btn.textContent ?? "";
    btn.textContent = busy ? "Please wait\u2026" : btn.dataset.label;
  }

  qsAll<HTMLElement>("[data-auth-nav]", shell).forEach((el) => {
    el.addEventListener("click", () => switchTo(el.dataset.authNav as AuthViewName));
  });

  /* ---------------------------- Login ---------------------------- */
  const loginForm = byId<HTMLFormElement>("login-form");
  loginForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    clearErrors();
    const email = byId<HTMLInputElement>("login-email").value.trim();
    const password = byId<HTMLInputElement>("login-password").value;
    if (!email || !password) {
      showError("login", "Enter your email and password.");
      return;
    }
    setBusy(loginForm, true);
    try {
      await authService.login({ email, password });
      loginForm.reset();
      onAuthenticated();
    } catch (err) {
      showError("login", err instanceof AuthError ? err.message : "Couldn't sign you in. Please try again.");
    } finally {
      setBusy(loginForm, false);
    }
  });

  /* --------------------------- Register --------------------------- */
  const registerForm = byId<HTMLFormElement>("register-form");
  registerForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    clearErrors();
    const name = byId<HTMLInputElement>("register-name").value.trim();
    const email = byId<HTMLInputElement>("register-email").value.trim();
    const phone = byId<HTMLInputElement>("register-phone").value.trim();
    const password = byId<HTMLInputElement>("register-password").value;
    const confirm = byId<HTMLInputElement>("register-password-confirm").value;
    const estateCode = byId<HTMLInputElement>("register-estate-code").value.trim();

    if (!name || !email || !phone || !password) {
      showError("register", "Please fill in every field.");
      return;
    }
    if (password.length < 8) {
      showError("register", "Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      showError("register", "Passwords don't match.");
      return;
    }

    setBusy(registerForm, true);
    try {
      await authService.register({ name, email, phone, password, estateCode: estateCode || undefined });
      registerForm.reset();
      onAuthenticated();
    } catch (err) {
      showError("register", err instanceof AuthError ? err.message : "Couldn't create your account. Please try again.");
    } finally {
      setBusy(registerForm, false);
    }
  });

  /* ------------------------ Forgot password ------------------------ */
  const forgotForm = byId<HTMLFormElement>("forgot-form");
  forgotForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    clearErrors();
    const email = byId<HTMLInputElement>("forgot-email").value.trim();
    if (!email) {
      showError("forgot", "Enter the email on your account.");
      return;
    }
    setBusy(forgotForm, true);
    try {
      await authService.requestPasswordReset({ email });
      byId("reset-sent-email").textContent = email;
      switchTo("reset-sent");
    } catch {
      showError("forgot", "Something went wrong. Please try again.");
    } finally {
      setBusy(forgotForm, false);
    }
  });

  /* ------------------------ Confirm reset ------------------------ */
  const resetForm = byId<HTMLFormElement>("reset-confirm-form");
  resetForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    clearErrors();
    const token = byId<HTMLInputElement>("reset-token").value.trim();
    const newPassword = byId<HTMLInputElement>("reset-new-password").value;
    if ((config.useMockServices && !token) || newPassword.length < 8) {
      showError("reset-confirm", config.useMockServices ? "Enter your reset code and a password of at least 8 characters." : "Enter a new password of at least 8 characters.");
      return;
    }
    setBusy(resetForm, true);
    try {
      await authService.confirmPasswordReset({ token, newPassword });
      resetForm.reset();
      switchTo("login");
      showError("login", "Password updated \u2014 sign in with your new password.");
      const el = views.login.querySelector<HTMLElement>(".auth-error");
      if (el) el.classList.add("auth-error--info");
    } catch (err) {
      showError("reset-confirm", err instanceof AuthError ? err.message : "That reset code didn't work. Please try again.");
    } finally {
      setBusy(resetForm, false);
    }
  });

  // Demo shortcut: with no real email delivery yet, the "sent" screen links
  // straight to the confirm-code screen so the reset flow can be tried
  // end-to-end. Real deployment replaces this with an emailed link.
  const demoHint = byId("demo-credentials-hint");
  if (config.useMockServices) {
    show(demoHint);
  } else {
    hide(demoHint);
  }

  // Real mode: the emailed link already proves who the person is, so there is no
  // code to type. Only ask for the new password.
  if (!config.useMockServices) {
    byId("reset-token-group").hidden = true;
    byId("reset-subheading").textContent = "Choose a new password for your account.";
  }

  addPasswordToggles();
  switchTo("login");

  return {
    showResetConfirm: () => {
      showAuthShell();
      switchTo("reset-confirm");
    },
  };
}

export function showAuthShell(): void {
  byId("auth-shell").hidden = false;
  byId("app").hidden = true;
}

export function hideAuthShell(): void {
  byId("auth-shell").hidden = true;
  byId("app").hidden = false;
}

/** Adds an eye button to every password box so people can check what they type. */
export function addPasswordToggles(root: ParentNode = document): void {
  root.querySelectorAll<HTMLInputElement>('input[type="password"]').forEach((input) => {
    if (input.dataset.eye) return;
    input.dataset.eye = "1";
    const wrap = document.createElement("span");
    wrap.className = "pw-wrap";
    input.parentNode!.insertBefore(wrap, input);
    wrap.appendChild(input);
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "pw-eye";
    btn.setAttribute("aria-label", "Show password");
    btn.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>';
    btn.addEventListener("click", () => {
      const show = input.type === "password";
      input.type = show ? "text" : "password";
      btn.classList.toggle("is-on", show);
      btn.setAttribute("aria-label", show ? "Hide password" : "Show password");
    });
    wrap.appendChild(btn);
  });
}
